import {useEffect,useMemo,useState} from 'react';
import {Helmet} from 'react-helmet-async';
import {Link,Navigate,useLocation,useParams} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import FlashcardPanel from './FlashcardPanel';
import QuizPanel from './QuizPanel';
import LessonContent from './LessonContent';
import ESYOHWidgets from './ESYOHWidgets';

const {FiArrowLeft,FiBookOpen,FiCheckCircle,FiClock}=FiIcons;
const SITE_URL='https://medicalcodingclassesonline.com';

function buildFaqEntities(lesson) {
  const flashcardFaqs=lesson.flashcards.map((flashcard)=> ({
    '@type': 'Question',
    name: flashcard.front,
    acceptedAnswer: {'@type': 'Answer',text: flashcard.back},
  }));
  const quizFaqs=lesson.quiz.map((question)=> ({
    '@type': 'Question',
    name: question.question,
    acceptedAnswer: {'@type': 'Answer',text: question.explanation},
  }));
  return [...flashcardFaqs,...quizFaqs];
}

function LessonView({
  domains,
  completedLessons,
  completionPercentage,
  onToggleLesson,
}) {
  const {domainId,lessonId}=useParams();
  const location=useLocation();
  const [activeTab,setActiveTab]=useState(location.state?.tab || 'content');
  const domain=domains.find((item)=> item.id===domainId);
  const lesson=domain?.lessons.find((item)=> item.id===lessonId);
  const isCompleted=completedLessons.includes(lessonId);

  const lessonPosition=useMemo(()=> {
    if (!domain || !lesson) return 0;
    return domain.lessons.findIndex((item)=> item.id===lessonId) + 1;
  },[domain,lesson,lessonId]);

  useEffect(()=> {
    window.prerenderReady=false;
    return ()=> {
      window.prerenderReady=false;
    };
  },[domainId,lessonId]);

  useEffect(()=> {
    setActiveTab(location.state?.tab || 'content');
    window.scrollTo({top: 0,behavior: 'auto'});
  },[lessonId,location.state]);

  useEffect(()=> {
    if (!lesson?.flashcards || !lesson?.quiz) return undefined;
    const frame=window.requestAnimationFrame(()=> {
      window.prerenderReady=true;
    });
    return ()=> window.cancelAnimationFrame(frame);
  },[lesson]);

  if (!domain || !lesson) {
    return <Navigate to="/" replace />;
  }

  const canonicalUrl=`${SITE_URL}/lesson/${domainId}/${lessonId}`;
  const description=lesson.summary || lesson.description || lesson.overview;
  const structuredData={
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Course',
        name: lesson.title,
        description: lesson.overview,
        provider: {
          '@type': 'Organization',
          name: 'Medical Coding Classes Online',
          sameAs: SITE_URL,
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: buildFaqEntities(lesson),
      },
    ],
  };

  const tabs=[
    {id: 'content',label: 'Lesson Content',icon: FiBookOpen},
    {id: 'flashcards',label: 'Flashcards',count: lesson.flashcards.length},
    {id: 'quiz',label: 'Practice Quiz',count: lesson.quiz.length},
  ];

  return (
    <>
      <Helmet>
        <title>{lesson.title} | Medical Coding Class Online</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={`${lesson.title} | Medical Coding Class Online`} />
        <meta property="og:description" content={description} />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={`${lesson.title} | Medical Coding Class Online`} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="lesson-view-page">
        <section className="lesson-view-hero">
          <div className="container">
            <Link className="back-link" to={`/module/${domain.id}`}>
              <SafeIcon icon={FiArrowLeft} />
              Back to Modules
            </Link>

            <div className="lesson-context">
              <div>
                <span className="eyebrow">{domain.title}</span>
                <span className="lesson-position">
                  Lesson {lessonPosition} of {domain.lessons.length}
                </span>
                <h1>{lesson.title}</h1>

                <div className="lesson-header-meta">
                  <span>
                    <SafeIcon icon={FiClock} />
                    {lesson.readTime} min read
                  </span>
                  <span>
                    <SafeIcon icon={isCompleted ? FiCheckCircle : FiBookOpen} />
                    {isCompleted ? 'Completed' : 'In progress'}
                  </span>
                </div>
              </div>

              <div
                className="overall-progress-badge"
                aria-label={`Overall progress ${completionPercentage}%`}
              >
                <strong>{completionPercentage}%</strong>
                <span>Overall progress</span>
              </div>
            </div>
          </div>
        </section>

        <section className="lesson-view-section">
          <div className="container lesson-view-container">
            <nav className="lesson-tabs" aria-label="Lesson study sections">
              {tabs.map((tab)=> (
                <button
                  className={activeTab===tab.id ? 'lesson-tab is-active' : 'lesson-tab'}
                  key={tab.id}
                  onClick={()=> setActiveTab(tab.id)}
                  aria-selected={activeTab===tab.id}
                  role="tab"
                >
                  <SafeIcon icon={tab.icon} />
                  <span>{tab.label}</span>
                  {tab.count && <small>{tab.count}</small>}
                </button>
              ))}
            </nav>

            <div className="lesson-tab-panel" role="tabpanel">
              {activeTab==='content' && (
                <LessonContent
                  lesson={lesson}
                  isCompleted={isCompleted}
                  onToggle={()=> onToggleLesson(lesson.id)}
                  onOpenTab={setActiveTab}
                />
              )}

              {activeTab==='flashcards' && (
                <FlashcardPanel flashcards={lesson.flashcards} />
              )}

              {activeTab==='quiz' && <QuizPanel lesson={lesson} />}
            </div>
          </div>
        </section>
        <div className="container" style={{ margin: '2rem auto' }}>
          <ESYOHWidgets widgetId="940985" />
        </div>
      </main>
    </>
  );
}

export default LessonView;